import React, { useState } from 'react';
import { X, Calendar, Clock, MapPin, FileText, User, Phone, CheckCircle, Edit, Unlock, UserCheck, Wrench, PartyPopper, Circle, Globe } from 'lucide-react';
import Modal from '../ui/Modal';
import { BlockedSlot } from '../../types/bookings';
import {
  formatDate,
  formatTime,
  formatDateTimeString,
  getBlockReasonDisplay,
} from '../../utils/bookingHelpers';

interface BlockedSlotDetailsModalProps {
  blockedSlot: BlockedSlot | null;
  isOpen: boolean;
  onClose: () => void;
  onCheckIn?: (blockId: number, customerName?: string) => Promise<void>;
  onEdit?: (blockId: number) => void;
  onUnblock?: (blockId: number) => Promise<void>;
}

// Helper function to get icon component for block reason
function getBlockReasonIconComponent(reason: string) {
  switch (reason) {
    case 'walk_in':
      return UserCheck;
    case 'phone_booking':
      return Phone;
    case 'maintenance':
      return Wrench;
    case 'private_event':
      return PartyPopper;
    case 'third_party':
      return Globe;
    default:
      return Circle;
  }
}

export default function BlockedSlotDetailsModal({
  blockedSlot,
  isOpen,
  onClose,
  onCheckIn,
  onEdit,
  onUnblock,
}: BlockedSlotDetailsModalProps) {
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [isUnblocking, setIsUnblocking] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  const [showUnblockConfirm, setShowUnblockConfirm] = useState(false);

  // Early return for null safety - must be after all hooks
  if (!blockedSlot) return null;

  const isPartnerBlock = blockedSlot.is_partner_block || blockedSlot.reason === 'third_party';
  const ReasonIcon = getBlockReasonIconComponent(blockedSlot.reason);
  const reasonDisplay = getBlockReasonDisplay(blockedSlot.reason, blockedSlot.partner_name);

  // Safe field access with optional chaining and defaults
  // Use flat court_name field from API, fallback to nested court.name, then 'N/A'
  const courtName = blockedSlot.court_name || blockedSlot.court?.name || 'N/A';

  const canCheckIn = !isPartnerBlock && blockedSlot.reason === 'walk_in' && blockedSlot.status !== 'checked_in';
  const canEdit = !isPartnerBlock && blockedSlot.status !== 'checked_in';
  // Partner blocks cannot be unblocked by vendors - only via the partner API
  const canUnblock = !isPartnerBlock && blockedSlot.status !== 'checked_in';

  const handleCheckIn = async () => {
    if (!onCheckIn) return;

    // If walk-in has no customer name, show input
    if (!blockedSlot.customer_name && !customerName && !showNameInput) {
      setShowNameInput(true);
      return;
    }

    setIsCheckingIn(true);
    try {
      await onCheckIn(blockedSlot.id, customerName || undefined);
      onClose();
    } catch (error) {
      console.error('Check-in failed:', error);
    } finally {
      setIsCheckingIn(false);
    }
  };

  const handleUnblock = async () => {
    if (!onUnblock) return;

    setIsUnblocking(true);
    try {
      await onUnblock(blockedSlot.id);
      setShowUnblockConfirm(false);
      onClose();
    } catch (error) {
      console.error('Unblock failed:', error);
    } finally {
      setIsUnblocking(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" animate={false}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className={`p-1.5 sm:p-2 rounded-lg ${isPartnerBlock ? 'bg-teal-100' : 'bg-orange-100'}`}>
              <ReasonIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${isPartnerBlock ? 'text-teal-600' : 'text-orange-600'}`} />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                {isPartnerBlock ? 'Third-Party Block' : 'Blocked Slot'}
              </h2>
              <p className="text-xs sm:text-sm text-gray-500">{reasonDisplay}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Status Badge */}
          <div className="flex gap-2 flex-wrap">
            <span
              className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                blockedSlot.status === 'released'
                  ? 'bg-red-100 text-red-800'
                  : blockedSlot.status === 'checked_in'
                  ? 'bg-green-100 text-green-800'
                  : isPartnerBlock
                  ? 'bg-teal-100 text-teal-800'
                  : 'bg-orange-100 text-orange-800'
              }`}
            >
              {blockedSlot.status === 'released'
                ? 'CANCELLED'
                : blockedSlot.status === 'checked_in'
                ? 'CHECKED IN'
                : isPartnerBlock
                ? 'THIRD-PARTY'
                : 'BLOCKED'}
            </span>
            <span className="px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-gray-100 text-gray-800">
              {reasonDisplay}
            </span>
          </div>

          {/* Slot Details */}
          <div className="bg-gray-50 rounded-xl sm:rounded-lg p-3 sm:p-4">
            <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              Slot Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <p className="text-xs sm:text-sm text-gray-500">Court</p>
                <p className="text-sm sm:text-base font-medium text-gray-900">{courtName}</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-500">Date</p>
                <p className="text-sm sm:text-base font-medium text-gray-900">{formatDate(blockedSlot.date)}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs sm:text-sm text-gray-500">Time</p>
                <p className="text-sm sm:text-base font-medium text-gray-900">
                  {formatTime(blockedSlot.start_time)} - {formatTime(blockedSlot.end_time)}
                </p>
              </div>
            </div>
          </div>

          {/* Partner Info (for third-party blocks) */}
          {isPartnerBlock && (
            <div className={`rounded-xl sm:rounded-lg p-3 sm:p-4 border ${
              blockedSlot.status === 'released'
                ? 'bg-red-50 border-red-200'
                : 'bg-teal-50 border-teal-200'
            }`}>
              <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                <Globe className={`w-4 h-4 sm:w-5 sm:h-5 ${blockedSlot.status === 'released' ? 'text-red-600' : 'text-teal-600'}`} />
                {blockedSlot.status === 'released' ? 'Cancelled Reservation' : 'Third-Party Partner'}
              </h3>
              <div className="space-y-2">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">Partner Name</p>
                  <p className="text-sm sm:text-base font-medium text-gray-900">{blockedSlot.partner_name || 'Unknown Partner'}</p>
                </div>
                {blockedSlot.block_reference && (
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Block Reference</p>
                    <p className="text-xs sm:text-sm font-medium text-gray-900 font-mono break-all">{blockedSlot.block_reference}</p>
                  </div>
                )}
                {blockedSlot.partner_reference && (
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Partner Reference</p>
                    <p className="text-xs sm:text-sm font-medium text-gray-900 font-mono break-all">{blockedSlot.partner_reference}</p>
                  </div>
                )}
                {blockedSlot.status === 'released' && blockedSlot.released_at && (
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Cancelled At</p>
                    <p className="text-sm font-medium text-gray-900">{formatDateTimeString(blockedSlot.released_at)}</p>
                  </div>
                )}
              </div>
              <p className={`text-xs mt-2 sm:mt-3 pt-2 sm:pt-3 border-t ${
                blockedSlot.status === 'released'
                  ? 'text-red-600 border-red-200'
                  : 'text-teal-600 border-teal-200'
              }`}>
                {blockedSlot.status === 'released'
                  ? 'Cancelled by partner. Slot now available.'
                  : 'Booked via third-party. Contact partner to release.'}
              </p>
            </div>
          )}

          {/* Customer Info (for walk-ins) */}
          {!isPartnerBlock && (blockedSlot.customer_name || blockedSlot.customer_phone || blockedSlot.reason === 'walk_in') && (
            <div className="bg-blue-50 rounded-xl sm:rounded-lg p-3 sm:p-4 border border-blue-200">
              <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                Customer Information
              </h3>
              <div className="space-y-2">
                {blockedSlot.customer_name ? (
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Name</p>
                    <p className="text-sm sm:text-base font-medium text-gray-900">{blockedSlot.customer_name}</p>
                  </div>
                ) : showNameInput ? (
                  <div>
                    <label className="text-xs sm:text-sm text-gray-500 block mb-1">Customer Name</label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Enter customer name"
                      className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-xl sm:rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      autoFocus
                    />
                  </div>
                ) : (
                  <p className="text-xs sm:text-sm text-gray-500">No customer name recorded</p>
                )}

                {blockedSlot.customer_phone && (
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Phone</p>
                    <a
                      href={`tel:${blockedSlot.customer_phone}`}
                      className="text-sm sm:text-base text-blue-600 hover:text-blue-700 flex items-center gap-2"
                    >
                      <Phone className="w-4 h-4" />
                      {blockedSlot.customer_phone}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {blockedSlot.notes && (
            <div className="bg-gray-50 rounded-xl sm:rounded-lg p-3 sm:p-4">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                Notes
              </h3>
              <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap">{blockedSlot.notes}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-gray-50 rounded-xl sm:rounded-lg p-3 sm:p-4">
            <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              Metadata
            </h3>
            <div className="space-y-2 text-xs sm:text-sm">
              {blockedSlot.created_at && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Created</span>
                  <span className="text-gray-900 text-right">{formatDateTimeString(blockedSlot.created_at)}</span>
                </div>
              )}
              {blockedSlot.checked_in_at && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Checked In</span>
                  <span className="text-gray-900 text-right">{formatDateTimeString(blockedSlot.checked_in_at)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Check-in Success Message */}
          {blockedSlot.status === 'checked_in' && (
            <div className="bg-green-50 rounded-xl sm:rounded-lg p-3 sm:p-4 border border-green-200">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                <p className="font-medium text-sm">Customer has been checked in</p>
              </div>
            </div>
          )}
        </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
          {canCheckIn && onCheckIn && (
            <button
              onClick={handleCheckIn}
              disabled={isCheckingIn || (showNameInput && !customerName)}
              className="w-full sm:flex-1 px-4 py-3 sm:py-2.5 bg-green-600 text-white rounded-xl sm:rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <CheckCircle className="w-4 h-4" />
              {isCheckingIn ? 'Checking In...' : 'Check In'}
            </button>
          )}

          {canEdit && onEdit && (
            <button
              onClick={() => onEdit(blockedSlot.id)}
              className="w-full sm:flex-1 px-4 py-3 sm:py-2.5 bg-blue-600 text-white rounded-xl sm:rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
          )}

          {canUnblock && onUnblock && (
            <button
              onClick={() => setShowUnblockConfirm(true)}
              disabled={isUnblocking}
              className="w-full sm:flex-1 px-4 py-3 sm:py-2.5 bg-red-600 text-white rounded-xl sm:rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Unlock className="w-4 h-4" />
              {isUnblocking ? 'Unblocking...' : 'Unblock'}
            </button>
          )}

          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-3 sm:py-2.5 bg-gray-200 text-gray-700 rounded-xl sm:rounded-lg font-medium hover:bg-gray-300 transition-colors text-sm"
          >
            Close
          </button>
        </div>

      {/* Custom Unblock Confirmation Dialog */}
      {showUnblockConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <Unlock className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Unblock Time Slot?</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to unblock this time slot? This will make it available for bookings again.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowUnblockConfirm(false)}
                  disabled={isUnblocking}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUnblock}
                  disabled={isUnblocking}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-red-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUnblocking ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Unblocking...
                    </>
                  ) : (
                    <>
                      <Unlock className="w-4 h-4" />
                      Yes, Unblock
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
